// Admin branding editor. A focused page (split out of the general settings
// hodge-podge) with three distinct sections:
//   1. Color palette — light + dark mode tokens with live WCAG contrast checks.
//   2. Landing page content — identity copy + hero + value-prop cards.
//   3. Intake form content — the main form page's header copy.
// Saving upserts only the branding-owned keys; region vocabulary stays on the
// Settings page and the pricing margin lives on the Pricing page.
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Alert, CircularProgress, Button, Paper, TextField, Stack,
  Tabs, Tab,
} from '@mui/material';
import {
  fetchAdminSettings, saveAdminSettings, clearSaveError,
} from '../../store/admin-settings-slice.js';
import { fetchPublicConfig } from '../../store/settings-slice.js';
import { fetchCatalog } from '../../store/catalog-slice.js';
import ValuePropsEditor from '../../components/admin/settings/ValuePropsEditor.jsx';
import PaletteModeEditor from '../../components/admin/branding/PaletteModeEditor.jsx';
import BrandingPreview from '../../components/admin/branding/BrandingPreview.jsx';
import LogoManager from '../../components/admin/branding/LogoManager.jsx';
import useUnsavedChangesGuard from '../../hooks/useUnsavedChangesGuard.js';
import { isHexColor } from '../../utils/wcag-contrast.js';
import { DEFAULT_THEME_PALETTE } from '../../theme.js';

// The landing-page copy fields, with hints about where each one renders.
const LANDING_FIELDS = [
  { key: 'program_name', label: 'Program name', hint: 'Overline above the hero headline; also the admin bar title.' },
  { key: 'page_title', label: 'Browser page title', hint: 'The browser tab / bookmark title.' },
  {
    key: 'meta_description', label: 'Meta description', hint: 'Search-engine snippet for the landing page.', multiline: true,
  },
  { key: 'hero_headline', label: 'Hero headline', hint: 'The large headline on the landing page.' },
  {
    key: 'hero_body', label: 'Hero body', hint: 'The paragraph under the headline.', multiline: true,
  },
];

const INTAKE_FIELDS = [
  { key: 'intake_header_title', label: 'Header title', hint: 'Bold title at the top of the form page.' },
  {
    key: 'intake_header_subtitle', label: 'Header subtitle', hint: 'Supporting line under the title.', multiline: true,
  },
];

// Branding owns these settings keys; the payload normalizer doubles as the
// dirty-check canonical form (trimmed strings, structured palette).
const buildPayload = (form) => ({
  program_name: (form.program_name || '').trim(),
  page_title: (form.page_title || '').trim(),
  meta_description: (form.meta_description || '').trim(),
  hero_headline: (form.hero_headline || '').trim(),
  hero_body: (form.hero_body || '').trim(),
  intake_header_title: (form.intake_header_title || '').trim(),
  intake_header_subtitle: (form.intake_header_subtitle || '').trim(),
  value_props: (form.value_props || []).map((p) => ({
    key: (p.key || '').trim(),
    icon: (p.icon || '').trim(),
    title: (p.title || '').trim(),
    body: (p.body || '').trim(),
  })),
  theme_palette: {
    light: { ...(form.theme_palette?.light || {}) },
    dark: { ...(form.theme_palette?.dark || {}) },
  },
});

const AdminBrandingPage = () => {
  const dispatch = useDispatch();
  const {
    settings, loading, error, saving, saveError,
  } = useSelector((s) => s.adminSettings);
  const [form, setForm] = useState(null);
  // Which palette mode tab is showing, and the preview-click focus signal
  // ({tokens, nonce}) forwarded to the field editor for flash + scroll.
  const [paletteTab, setPaletteTab] = useState('light');
  const [paletteFocus, setPaletteFocus] = useState(null);

  useEffect(() => { dispatch(fetchAdminSettings()); }, [dispatch]);

  // The palette preview renders real products, so make sure the public
  // catalog is loaded.
  const catalogLoaded = useSelector((s) => Boolean(s.catalog.data) || s.catalog.loading);
  useEffect(() => {
    if (!catalogLoaded) dispatch(fetchCatalog());
  }, [catalogLoaded, dispatch]);

  // Seed the form once settings arrive (and after a save resyncs them).
  useEffect(() => {
    if (settings) setForm(structuredClone(settings));
  }, [settings]);

  const dirty = useMemo(() => {
    if (!settings || !form) return false;
    return JSON.stringify(buildPayload(form)) !== JSON.stringify(buildPayload(settings));
  }, [form, settings]);

  // Every palette token in both modes must be a valid hex before saving;
  // otherwise the server would reject the payload anyway.
  const paletteValid = useMemo(() => {
    if (!form) return true;
    return ['light', 'dark'].every((mode) => Object.keys(DEFAULT_THEME_PALETTE[mode])
      .every((key) => isHexColor(form.theme_palette?.[mode]?.[key])));
  }, [form]);

  useUnsavedChangesGuard(dirty);

  if (loading && !form) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress aria-label="Loading branding settings" />
      </Box>
    );
  }

  if (error && !form) return <Alert severity="error">{error}</Alert>;
  if (!form) return null;

  const setField = (key, value) => {
    if (saveError) dispatch(clearSaveError());
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setPaletteMode = (mode, palette) => {
    if (saveError) dispatch(clearSaveError());
    setForm((prev) => ({
      ...prev,
      theme_palette: { ...prev.theme_palette, [mode]: palette },
    }));
  };

  const handleSave = async () => {
    const result = await dispatch(saveAdminSettings(buildPayload(form)));
    // Refresh the public config so the live theme + copy update immediately.
    if (saveAdminSettings.fulfilled.match(result)) dispatch(fetchPublicConfig());
  };

  const handleReset = () => setForm(structuredClone(settings));

  const renderFields = (fields) => (
    <Stack spacing={2}>
      {fields.map((field) => (
        <TextField
          key={field.key}
          label={field.label}
          value={form[field.key] ?? ''}
          onChange={(e) => setField(field.key, e.target.value)}
          multiline={field.multiline}
          minRows={field.multiline ? 2 : undefined}
          helperText={field.hint}
          fullWidth
        />
      ))}
    </Stack>
  );

  return (
    <Box>
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap',
      }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Branding
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Colors and content for the public portal.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          {dirty && <Typography variant="body2" color="text.secondary">Unsaved changes</Typography>}
          <Button onClick={handleReset} disabled={!dirty || saving}>Reset</Button>
          <Button variant="contained" onClick={handleSave} disabled={!dirty || saving || !paletteValid}>
            {saving ? <CircularProgress size={22} color="inherit" /> : 'Save changes'}
          </Button>
        </Stack>
      </Box>

      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
            Partner logos
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Agency or partner logos shown next to the Sanborn mark on the landing
            page, the intake form header, and outgoing emails. They appear in the
            order below; drag is replaced by the up/down controls. Saving a logo
            updates the live portal immediately.
          </Typography>
          <LogoManager />
        </Paper>

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
            Color palette
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The portal ships light and dark themes; visitors get their system
            mode by default and can toggle. Each combination is checked against
            WCAG 2.0 contrast guidelines as you edit — failing pairs can be hard
            to read, including for color-blind visitors. Click any part of the
            preview to jump to the colors that paint it.
          </Typography>
          <Tabs
            value={paletteTab}
            onChange={(e, v) => { setPaletteTab(v); setPaletteFocus(null); }}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Light mode" value="light" />
            <Tab label="Dark mode" value="dark" />
          </Tabs>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <BrandingPreview
              key={paletteTab}
              mode={paletteTab}
              palette={form.theme_palette?.[paletteTab]}
              content={{
                intakeHeaderTitle: form.intake_header_title,
                intakeHeaderSubtitle: form.intake_header_subtitle,
              }}
              onSelectTokens={(tokens) => setPaletteFocus({ tokens, nonce: Date.now() })}
            />
            <Box sx={{ flex: '0 1 360px', minWidth: 300 }}>
              <PaletteModeEditor
                key={`editor-${paletteTab}`}
                title={paletteTab === 'light' ? 'Light mode colors' : 'Dark mode colors'}
                palette={form.theme_palette?.[paletteTab]}
                defaults={DEFAULT_THEME_PALETTE[paletteTab]}
                onChange={(p) => setPaletteMode(paletteTab, p)}
                focus={paletteFocus}
              />
            </Box>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
            Landing page
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The first page visitors see: program identity, the hero pitch, and
            the value-prop highlight cards.
          </Typography>
          {renderFields(LANDING_FIELDS)}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
            Value props
          </Typography>
          <ValuePropsEditor
            items={form.value_props ?? []}
            onChange={(next) => setField('value_props', next)}
          />
        </Paper>

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
            Intake form page
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The header copy on the main form page where visitors pick an area
            and products.
          </Typography>
          {renderFields(INTAKE_FIELDS)}
        </Paper>
      </Stack>
    </Box>
  );
};

export default AdminBrandingPage;
